// src/components/EcommerceModule.js
import React, { useState, useEffect, useCallback } from 'react';
import OrderDetailModal from './OrderDetailModal';
import { useAuth } from '../context/AuthContext';
import GuideRequiredModal from './GuideRequiredModal';

// ===== Base de API robusta: prod => mismo origin; dev => localhost:3000
const API_BASE =
  (process.env.REACT_APP_API_URL &&
    process.env.REACT_APP_API_URL.replace(/\/+$/, '')) ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:3000'
    : window.location.origin);

// Este componente ahora solo renderiza el contenido principal del módulo Ecommerce.
// La barra lateral y el header son manejados por AppLayout.
function EcommerceModule({ onLogout, navigateTo, currentView }) {
  const { user, token } = useAuth(); // usuario y token del contexto
  console.log('EcommerceModule: Usuario obtenido del contexto:', user);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncMessage, setSyncMessage] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);

  // --- Filtros y paginación ---
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrdersCount, setTotalOrdersCount] = useState(0);
  const ordersPerPage = 15;
  const maxPageButtons = 12;

  // --- Pestaña activa de plataforma ---
  const [activePlatformTab, setActivePlatformTab] = useState('Todos'); // 'Todos', 'Mercado Libre', 'Walmart', 'COPPEL'

  // --- Modal detalles ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // --- Modal "se requiere guía" ---
  const [showGuideRequiredModal, setShowGuideRequiredModal] = useState(false);

  // Colores por estatus
  const getStatusColorClass = (status) => {
    switch (status) {
      case 'Confirmada': return 'bg-green-500';
      case 'Preparación': return 'bg-orange-500';
      case 'Listo para envío': return 'bg-blue-500';
      case 'Enviada': return 'bg-teal-500';
      case 'Cancelada': return 'bg-red-500';
      case 'Pendiente de factura': return 'bg-purple-500';
      case 'Inventario Pendiente': return 'bg-yellow-500';
      default: return 'bg-gray-400';
    }
  };

  // Valida guía usable para transición
  const isValidGuideForTransition = (guideNumber) => {
    if (!guideNumber || typeof guideNumber !== 'string') return false;
    const cleanedGuide = guideNumber.trim().toUpperCase();
    return cleanedGuide !== '' &&
           cleanedGuide !== 'N/A' &&
           !/^GUIA-\d+-\d+$/.test(guideNumber) &&
           !cleanedGuide.includes('PENDING') &&
           !cleanedGuide.includes('GENERATING');
  };

  // Obtener pedidos
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: ordersPerPage,
      });
      if (searchTerm) queryParams.append('searchTerm', searchTerm);
      if (fromDate) queryParams.append('fromDate', fromDate);
      if (toDate) queryParams.append('toDate', toDate);
      if (statusFilter) queryParams.append('status', statusFilter);
      if (activePlatformTab !== 'Todos') {
        queryParams.append('canal_origen', activePlatformTab);
      }

      const response = await fetch(`${API_BASE}/api/orders?${queryParams.toString()}`, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        credentials: 'include',
      });

      const ct = response.headers.get('content-type') || '';
      const isJson = ct.includes('application/json');
      const payload = isJson ? await response.json() : await response.text();

      if (response.ok) {
        const data = isJson ? payload : { orders: [], totalOrders: 0, totalPages: 1 };
        setOrders(data.orders || []);
        setTotalOrdersCount(data.totalOrders || 0);
        setTotalPages(data.totalPages || 1);
      } else {
        setError((isJson && (payload?.mensaje || payload?.message)) || `Error al cargar pedidos (${response.status})`);
      }
    } catch (err) {
      console.error('EcommerceModule: Error de red al obtener pedidos:', err);
      setError('Error de conexión con el servidor de pedidos.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, fromDate, toDate, statusFilter, activePlatformTab, token]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Volver a la página 1 al cambiar filtros
  useEffect(() => { setCurrentPage(1); }, [searchTerm, fromDate, toDate, statusFilter, activePlatformTab]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) setCurrentPage(pageNumber);
  };

  const formatMercadoLibreGuide = (guideNumber) => {
    if (!guideNumber) return 'N/A';
    const match = guideNumber.match(/MEL(\d+)(?:LMXDF01|FMDOF01)/);
    if (match && match[1]) return match[1];
    return guideNumber;
  };

  const openModal = (order) => { setSelectedOrder(order); setIsModalOpen(true); };
  const closeModal = () => { setIsModalOpen(false); setSelectedOrder(null); fetchOrders(); };

  // Exportar CSV
  const handleExportClick = async () => {
    try {
      const exportQueryParams = new URLSearchParams();
      if (searchTerm) exportQueryParams.append('searchTerm', searchTerm);
      if (fromDate) exportQueryParams.append('fromDate', fromDate);
      if (toDate) exportQueryParams.append('toDate', toDate);
      if (statusFilter) exportQueryParams.append('status', statusFilter);
      if (activePlatformTab !== 'Todos') {
        exportQueryParams.append('canal_origen', activePlatformTab);
      }
      const exportUrl = `${API_BASE}/api/orders/export?${exportQueryParams.toString()}`;

      const response = await fetch(exportUrl, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        credentials: 'include',
      });

      if (!response.ok) {
        const errJson = (response.headers.get('content-type') || '').includes('application/json')
          ? await response.json()
          : { mensaje: await response.text() };
        alert(`Error al exportar pedidos: ${errJson.mensaje || 'Error desconocido'}`);
        return;
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'pedidos.csv';
      if (contentDisposition && contentDisposition.indexOf('filename=') !== -1) {
        filename = contentDisposition.split('filename=')[1].split(';')[0].replace(/"/g, '');
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      alert('Exportación completada exitosamente.');
    } catch (err) {
      console.error('Error de red al exportar pedidos:', err);
      alert('Error de conexión al exportar pedidos.');
    }
  };

  const handleGoToOrderDetails = (order) => {
    setShowGuideRequiredModal(false);
    openModal(order);
  };

  // Clic en estatus
  const handleStatusCircleClick = async (order) => {
    const currentInternalStatus = order.estado_interno;
    let nextStatus = '';
    let statusChangeAllowed = true;
    let message = '';

    const hasValidPrintedGuide = order.guides?.some(guide =>
      guide.print_status === 1 && isValidGuideForTransition(guide.guide_number)
    );
    const hasAnyValidGuide = order.guides?.some(guide =>
      isValidGuideForTransition(guide.guide_number)
    );

    switch (currentInternalStatus) {
      case 'Confirmada':
        nextStatus = 'Preparación';
        if (user?.rol !== 'admin' && !hasValidPrintedGuide) {
          statusChangeAllowed = false;
          setSelectedOrder(order);
          setShowGuideRequiredModal(true);
          return;
        }
        break;
      case 'Preparación':
        nextStatus = 'Listo para envío';
        break;
      case 'Listo para envío':
        nextStatus = 'Enviada';
        if (user?.rol !== 'admin' && !hasAnyValidGuide) {
          statusChangeAllowed = false;
          message = 'No se puede pasar a "Enviada" porque no hay una guía válida asociada al pedido.';
        }
        break;
      case 'Inventario Pendiente':
      case 'Cancelada':
      case 'Enviada':
      case 'Pendiente de factura':
        statusChangeAllowed = false;
        message = `El estado "${currentInternalStatus}" no puede avanzar con un clic.`;
        break;
      default:
        statusChangeAllowed = false;
        message = `Transición no definida para el estado "${currentInternalStatus}".`;
        break;
    }

    if (!statusChangeAllowed) { alert(message); return; }

    try {
      const response = await fetch(`${API_BASE}/api/orders/${order.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({
          estado_interno: nextStatus,
          performed_by_user_id: user?.id || null,
          performed_by_username: user?.nombre || 'Desconocido'
        })
      });

      const data = await response.json();
      if (response.ok) {
        alert(`Estatus actualizado a "${nextStatus}" exitosamente.`);
        fetchOrders();
      } else {
        alert(`Error al actualizar estatus: ${data.mensaje || 'Error desconocido'}`);
      }
    } catch {
      alert('Error de conexión al intentar actualizar estatus.');
    }
  };

  // Transición automática de estado tras imprimir guía
  const handleAutomaticStatusTransition = async (orderId, targetStatus) => {
    try {
      const response = await fetch(`${API_BASE}/api/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({
          estado_interno: targetStatus,
          performed_by_user_id: user?.id || null,
          performed_by_username: user?.nombre || 'Desconocido'
        })
      });
      const data = await response.json();
      if (response.ok) {
        console.log(`Estatus actualizado automáticamente a "${targetStatus}" para pedido ${orderId}.`);
        fetchOrders();
      } else {
        console.error(`Error al actualizar estatus automáticamente para pedido ${orderId}: ${data.mensaje || 'Error desconocido'}`);
        alert(`Error al actualizar estatus automáticamente: ${data.mensaje || 'Error desconocido'}`);
      }
    } catch (err) {
      console.error('Error de conexión al intentar actualizar estatus automáticamente:', err);
      alert('Error de conexión al intentar actualizar estatus automáticamente.');
    }
  };

  // Sincronizar con AutoAzur
  const handleSyncAutoAzur = async () => {
    // --- NUEVA VALIDACIÓN ---
    // Si no hay token, no continuar.
    if (!token) {
        alert('Tu sesión ha expirado o no es válida. Por favor, vuelve a iniciar sesión.');
        setIsSyncing(false); // Asegurarse de que el estado de 'syncing' no se quede atascado
        setSyncMessage('Error: Sesión no válida.');
        return; // Detiene la ejecución de la función aquí
    }

    setIsSyncing(true);
    setSyncMessage('Sincronizando pedidos con AutoAzur...');
    try {
      // Calcula el rango de fechas para los últimos 20 días
      const toDateObj = new Date();
      const fromDateObj = new Date();
      fromDateObj.setDate(toDateObj.getDate() - 30);

      // Formatea las fechas a YYYY-MM-DD
      const formatDate = (date) => date.toISOString().split('T')[0];
      const dynamicToDate = formatDate(toDateObj);
      const dynamicFromDate = formatDate(fromDateObj);

      const response = await fetch(`${API_BASE}/api/orders/sync-from-autoazur`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ fromDate: dynamicFromDate, toDate: dynamicToDate })
      });

      const ct = response.headers.get('content-type') || '';
      const isJson = ct.includes('application/json');
      const data = isJson ? await response.json() : { mensaje: await response.text() };

      if (response.ok) {
        const resumen = data?.resumen || {};
        setSyncMessage(`Sincronización completada: ${resumen.insertados || 0} insertados, ${resumen.actualizados || 0} actualizados.`);
        setTimeout(() => { fetchOrders(); }, 1000);
      } else {
        // Manejo de error más específico
        if(response.status === 401 || response.status === 403){
            setSyncMessage(`Error de autenticación. Tu sesión puede haber expirado.`);
        } else {
            setSyncMessage(`Error en la sincronización: ${data.mensaje || 'Error desconocido'}`);
        }
      }
    } catch (err) {
      setSyncMessage('Error de conexión con el servidor al sincronizar.');
    } finally {
      setIsSyncing(false);
      setTimeout(() => setSyncMessage(''), 5000);
    }
  };

  // Paginación
  const renderPaginationButtons = () => {
    const buttons = [];
    let startPage, endPage;

    if (totalPages <= maxPageButtons) {
      startPage = 1; endPage = totalPages;
    } else {
      const maxPagesBeforeCurrent = Math.floor(maxPageButtons / 2);
      const maxPagesAfterCurrent = Math.ceil(maxPageButtons / 2) - 1;
      if (currentPage <= maxPagesBeforeCurrent) {
        startPage = 1; endPage = maxPageButtons;
      } else if (currentPage + maxPagesAfterCurrent >= totalPages) {
        startPage = totalPages - maxPageButtons + 1; endPage = totalPages;
      } else {
        startPage = currentPage - maxPagesBeforeCurrent;
        endPage = currentPage + maxPagesAfterCurrent;
      }
    }

    if (startPage > 1) {
      buttons.push(<button key={1} onClick={() => handlePageChange(1)} className="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100">1</button>);
      if (startPage > 2) buttons.push(<span key="dots-start" className="px-3 py-1">...</span>);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-lg border ${currentPage === i ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) buttons.push(<span key="dots-end" className="px-3 py-1">...</span>);
      buttons.push(<button key={totalPages} onClick={() => handlePageChange(totalPages)} className="px-3 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100">{totalPages}</button>);
    }

    return buttons;
  };

  return (
    <>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Consulta de Pedidos</h2>

      <div className="bg-white p-2 rounded-lg shadow mb-6">
        <nav className="flex space-x-2">
          {['Todos', 'Mercado Libre', 'Walmart', 'COPPEL'].map(platform => (
            <button
              key={platform}
              onClick={() => setActivePlatformTab(platform)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200
                ${activePlatformTab === platform ? 'bg-blue-600 text-white shadow-md' : 'text-gray-700 hover:bg-gray-100'}
              `}
            >
              {platform}
            </button>
          ))}
        </nav>
      </div>

      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label htmlFor="search" className="block text-gray-700 text-sm font-bold mb-2">Buscar</label>
            <input
              type="text" id="search"
              className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700"
              placeholder="# pedido / Cliente / Factura"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="fromDate" className="block text-gray-700 text-sm font-bold mb-2">Fecha Inicio</label>
            <input
              type="date" id="fromDate"
              className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="toDate" className="block text-gray-700 text-sm font-bold mb-2">Fin</label>
            <input
              type="date" id="toDate"
              className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="status" className="block text-gray-700 text-sm font-bold mb-2">Estatus</label>
            <select
              id="status"
              className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Todos</option>
              {['Inventario Pendiente', 'Confirmada', 'Preparación', 'Listo para envío', 'Enviada', 'Cancelada', 'Pendiente de factura'].map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 flex justify-end space-x-2">
          <button onClick={handleExportClick} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg">
            Exportar
          </button>
          <button
            onClick={handleSyncAutoAzur}
            disabled={isSyncing}
            className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg disabled:bg-gray-400"
          >
            {isSyncing ? 'Sincronizando...' : 'Sincronizar AutoAzur'}
          </button>
        </div>

        {syncMessage && (
          <p className={`text-sm mt-2 text-right ${syncMessage.includes('Error') ? 'text-red-600' : 'text-green-700'}`}>
            {syncMessage}
          </p>
        )}
      </div>

      {loading && <p className="text-center">Cargando pedidos...</p>}
      {error && <p className="text-center text-red-500">{error}</p>}

      {!loading && !error && (
        <>
          <div className="bg-white p-6 rounded-lg shadow mb-6 overflow-x-auto">
            {orders.length === 0 ? (
              <p className="text-center text-gray-600">No se encontraron pedidos con los filtros aplicados.</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"># Pedido</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Factura</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plataforma</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cotización</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estatus</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Guía(s)</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => {
                    const MAX_CLIENT_NAME_LENGTH = 20;
                    const displayClientName =
                      order.cliente_nombre.length > MAX_CLIENT_NAME_LENGTH
                        ? order.cliente_nombre.substring(0, MAX_CLIENT_NAME_LENGTH) + '...'
                        : order.cliente_nombre;

                    return (
                      <tr key={order.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button onClick={() => openModal(order)} className="text-blue-600 hover:underline">
                            {order.referencia_plataforma}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {new Date(order.fecha_venta).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" title={order.cliente_nombre}>
                          {displayClientName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {order.numero_factura ? (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                              {order.numero_factura}
                            </span>
                          ) : (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                              N/A
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{order.canal_origen}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {order.picking_quote_number ? (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                              {order.picking_quote_number}
                            </span>
                          ) : (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                              N/A
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => handleStatusCircleClick(order)}
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColorClass(order.estado_interno)} text-white`}
                            title="Clic para avanzar estado"
                          >
                            {order.estado_interno}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {order.guides && order.guides.length > 0 ? (
                            <div className="flex flex-col items-start">
                              {order.guides.map((guide, index) => (
                                <span key={guide.id || index} className="text-gray-700 text-xs">
                                  {order.canal_origen === 'MERCADO LIBRE'
                                    ? formatMercadoLibreGuide(guide.guide_number)
                                    : guide.guide_number || 'N/A'}
                                  {guide.print_status === 1 && <span className="ml-1 text-green-500" title="Impresa">&#10003;</span>}
                                  {guide.print_status === 0 && <span className="ml-1 text-red-500" title="No Impresa">&#10007;</span>}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-500">N/A</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Paginación */}
          <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow">
            <p className="text-sm text-gray-700">
              Mostrando {((currentPage - 1) * ordersPerPage) + 1} - {Math.min(currentPage * ordersPerPage, totalOrdersCount)} de {totalOrdersCount} pedidos
            </p>
            <div className="flex items-center space-x-1">
              <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 rounded-lg border border-gray-300 disabled:opacity-50">&laquo;</button>
              {renderPaginationButtons()}
              <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 rounded-lg border border-gray-300 disabled:opacity-50">&raquo;</button>
            </div>
          </div>
        </>
      )}

      {isModalOpen && selectedOrder && (
        <OrderDetailModal order={selectedOrder} onClose={closeModal} user={user || {}} />
      )}

      {showGuideRequiredModal && selectedOrder && (
        <GuideRequiredModal
          order={selectedOrder}
          onClose={() => setShowGuideRequiredModal(false)}
          onGoToOrderDetails={handleGoToOrderDetails}
          user={user || {}}
          onRefreshOrders={fetchOrders}
          onGuidePrintedAndTransition={handleAutomaticStatusTransition}
        />
      )}
    </>
  );
}

export default EcommerceModule;

